/**
 * Brand logo manifest — fetches the DB-backed brand→logo mapping
 * from the SP backend and caches it module-globally so any caller
 * (autoLayout for the title block, PropertiesPanel for the brand
 * cards, etc.) can do a synchronous lookup.
 *
 * Source of truth is the existing `tbl_logos` table the rest of
 * the project's controllers (BatteriesController,
 * InvertersController, SolarPanelsController) join against. We
 * shouldn't be inventing parallel logo storage — this just exposes
 * the same data to the SLD frontend.
 *
 * The manifest is normalised to lowercase manufacturer names so
 * lookups are case-insensitive.
 */

import { sldApiUrl } from '../api/sldApiUrl';

export interface BrandLogoManifest {
  panels:    Record<string, string>;
  inverters: Record<string, string>;
  batteries: Record<string, string>;
}

let cached: BrandLogoManifest = {
  panels: {},
  inverters: {},
  batteries: {},
};
let loaded = false;
let loading: Promise<void> | null = null;

// Subscribers notified when the manifest finishes loading. Used by
// the title-block render path to trigger a re-layout once real
// logos are available — without this the very first render shows
// initials and the user has to nudge the topology to refresh.
const subscribers = new Set<() => void>();
export function onBrandLogosLoaded(cb: () => void): () => void {
  subscribers.add(cb);
  if (loaded) cb();
  return () => subscribers.delete(cb);
}

/**
 * Kick off the fetch (idempotent — second call returns the same
 * in-flight promise). Call this once from main.tsx so the manifest
 * is ready by the time autoLayout renders.
 *
 * Failures are swallowed — the title block falls back to initials
 * badges if the endpoint isn't reachable. We don't want to block
 * SLD rendering on a backend round-trip.
 */
export function loadBrandLogos(): Promise<void> {
  if (loaded) return Promise.resolve();
  if (loading) return loading;
  loading = fetch(sldApiUrl('brand-logos.php'))
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      if (data && typeof data === 'object') {
        // Re-index each entry under EVERY variant produced by
        // brandKeys (lowercase, alnum-only, first-word). The
        // backend may return "JinkoSolar" while the catalogue
        // looks up "Jinko Solar" — indexing both sides catches
        // either direction.
        const expand = (raw: Record<string, string> = {}) => {
          const out: Record<string, string> = {};
          for (const [name, url] of Object.entries(raw)) {
            for (const k of brandKeys(name)) {
              if (!out[k]) out[k] = url;
            }
          }
          return out;
        };
        cached = {
          panels:    expand(data.panels    as Record<string, string>),
          inverters: expand(data.inverters as Record<string, string>),
          batteries: expand(data.batteries as Record<string, string>),
        };
      }
      loaded = true;
      subscribers.forEach((cb) => { try { cb(); } catch { /* ignore */ } });
    })
    .catch(() => {
      // Backend unreachable — fall back to initials badges.
      loaded = true;
      subscribers.forEach((cb) => { try { cb(); } catch { /* ignore */ } });
    });
  return loading;
}

/**
 * Normalise a brand name into multiple progressively-looser keys
 * for matching. The DB's `tbl_logos.manufacturer` is whatever the
 * user typed when uploading; the SLD catalog uses tidier names.
 * Trying these variants in order finds matches across most casual
 * spelling differences:
 *
 *   "Jinko Solar"  →  ["jinko solar", "jinkosolar", "jinko"]
 *   "JinkoSolar"   →  ["jinkosolar",  "jinkosolar", "jinkosolar"]
 *   "Tesla"        →  ["tesla",       "tesla",      "tesla"]
 *   "JA Solar"     →  ["ja solar",    "jasolar",    "ja"]
 */
function brandKeys(name: string): string[] {
  const lower = name.toLowerCase().trim();
  if (!lower) return [];
  const stripped = lower.replace(/[^a-z0-9]+/g, '');
  const firstWord = lower.split(/\s+/)[0] ?? '';
  // De-dupe while preserving order — exact lowercase wins when present.
  const out: string[] = [];
  for (const k of [lower, stripped, firstWord]) {
    if (k && !out.includes(k)) out.push(k);
  }
  return out;
}

/**
 * Synchronous lookup — returns the absolute URL for a manufacturer
 * logo, or undefined when no match is registered.
 *
 * Tries three progressively-looser matches: exact lowercase, then
 * alphanumeric-only, then just the first word. This forgives the
 * common spelling variations between the SLD catalogue ("Jinko
 * Solar") and what the user typed in tbl_logos ("JinkoSolar",
 * "Jinko", etc.) without per-brand alias maintenance.
 *
 * The cached manifest is also indexed under all its variants when
 * loaded — see loadBrandLogos — so this lookup is O(1) per try.
 */
export function getBrandLogoUrl(
  kind: 'panels' | 'inverters' | 'batteries',
  manufacturer: string,
): string | undefined {
  if (!manufacturer) return undefined;
  const bucket = cached[kind];
  for (const k of brandKeys(manufacturer)) {
    const hit = bucket[k];
    if (hit) return hit;
  }
  return undefined;
}

/**
 * Read-only access to the whole manifest — used by tests / debug
 * tooling. Avoid in render paths; prefer getBrandLogoUrl().
 */
export function getBrandLogoManifest(): BrandLogoManifest {
  return cached;
}
